import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useFlagsmith } from 'flagsmith/react';

import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';
import { Loading } from '~/components/Loading';
import { useUserProfile } from '~/services/useUserProfileQuery';
import { type FlagsmithTraits, type IdentityState, initialState, reducer } from './flagsmithIdentityState';

type FlagsmithIdentityContextValue = {
  isLoading: boolean;
};

export const FlagsmithIdentityContext = createContext<FlagsmithIdentityContextValue>({ isLoading: true });

// Re-exported so callers have a unified API rather than be exposed to the state machine,
// which is an internal implementation detail.
export type { IdentityState };

const log = createLogger('FlagsmithIdentityProvider');

export function FlagsmithIdentityProvider({ children }: PropsWithChildren) {
  const { user, isLoading: isUserProfileLoading } = useUserProfile();
  const flagsmith = useFlagsmith();

  const traits = useMemo<FlagsmithTraits>(() => {
    if (!user) return {};
    const { email, name, role, schoolId } = user;
    return { email, name, role, schoolId };
  }, [user]);

  const [identity, dispatch] = useReducer(reducer, initialState);

  // -------------------------------------------------------------------------
  // Side effects — only place that touches the Flagsmith SDK
  // -------------------------------------------------------------------------

  const fetchAnonymousFlags = useCallback(async () => {
    if (identity.fetchedAnonymous) return;
    dispatch({ type: 'FETCH_STARTED', anonymous: true });
    try {
      await flagsmith.getFlags();
      dispatch({ type: 'FETCH_SUCCEEDED', anonymous: true });
    } catch (err) {
      log.error('Flagsmith getFlags (anonymous) failed', {
        errorMessage: sanitizeTagValue(err instanceof Error ? err.message : 'Unknown error'),
      });
      dispatch({ type: 'FETCH_FAILED', anonymous: true });
    }
  }, [identity.fetchedAnonymous, flagsmith]);

  const logout = useCallback(async () => {
    try {
      // flagsmith.logout() will re-fetch the default (anonymous) flags.
      await flagsmith.logout();
    } catch (err) {
      log.error('Flagsmith logout failed', {
        errorMessage: sanitizeTagValue(err instanceof Error ? err.message : 'Unknown error'),
      });
    }
    dispatch({ type: 'LOGOUT_COMPLETE' });
  }, [flagsmith]);

  const login = useCallback(
    async (email: string) => {
      dispatch({ type: 'FETCH_STARTED', anonymous: false, email });
      log.debug('Flagsmith identify started');

      try {
        await flagsmith.identify(email, traits);
        log.info('Flagsmith identify succeeded');
        dispatch({ type: 'FETCH_SUCCEEDED', anonymous: false, email });
      } catch (err) {
        log.error('Flagsmith identify failed', {
          errorMessage: sanitizeTagValue(err instanceof Error ? err.message : 'Unknown error'),
        });
        dispatch({ type: 'FETCH_FAILED', anonymous: false });
      }
    },
    [flagsmith, traits],
  );

  const { prevEmail, attemptedEmail, loading } = identity;

  // True if the user has an email but Flagsmith hasn't yet started an identify() call for
  // it. This closes the window between "user profile resolves" and "FETCH_STARTED dispatch"
  // where children would otherwise render with stale anonymous flags.
  const pendingIdentify = !!traits.email && attemptedEmail !== traits.email;

  useEffect(() => {
    if (isUserProfileLoading) return;

    const email = traits.email;

    if (!email && prevEmail === undefined) {
      fetchAnonymousFlags();
      return;
    }

    if (!email && prevEmail !== undefined) {
      logout();
      return;
    }

    if (email && attemptedEmail !== email) {
      login(email);
    }
  }, [isUserProfileLoading, traits, prevEmail, attemptedEmail, fetchAnonymousFlags, login, logout]);

  const isLoading = loading || pendingIdentify;

  return (
    <FlagsmithIdentityContext.Provider value={{ isLoading }}>
      {isLoading ? <Loading /> : children}
    </FlagsmithIdentityContext.Provider>
  );
}
